import {
  KeyboardEvent,
  Fragment,
  useEffect,
  useState,
  useCallback,
} from 'react';

import { Combobox } from '@headlessui/react';
import { IconPlus, IconZoomOutline } from '@iwoca/icons';
import {
  fetchGetPostcodeLookup,
  GetPostcodeLookupResponse,
} from '@iwoca/lapi-client/edge';
import { Button, Input, Spinner } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';

import { buildAddressLabel } from './addressBuilder';
import {
  stripPostcodeFromAddress,
  verifyUKCrownPostcode,
  verifyUKPostcode,
} from './addressHelpers';
import { ManualAddressInput } from './ManualAddressInput';
import styles from './PostcodeInput.module.css';
import ButtonLink from '../../../../ButtonLink/ButtonLink';
import { TPersonalDetailsDataUK } from '../../PersonalDetailsUK';

type TAddress = GetPostcodeLookupResponse['data'][number];

type TProps = {
  addressIndex: number;
  postcodeLabel?: string;
  renderRemoveAddressComponent?: () => React.ReactElement;
};

export const PostcodeInput = ({
  addressIndex,
  postcodeLabel,
  renderRemoveAddressComponent,
}: TProps) => {
  const {
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<TPersonalDetailsDataUK>();

  const INDEXED_ADDRESS = `residentialAddresses.${addressIndex}` as const;

  const [addresses, setAddresses] = useState<GetPostcodeLookupResponse['data']>(
    [],
  );

  const [isManualAddress, setIsManualAddress] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [showDefaultAddress, setShowDefaultAddress] = useState(
    Boolean(
      getValues(`${INDEXED_ADDRESS}.streetLine1`) &&
      getValues(`${INDEXED_ADDRESS}.town`) &&
      getValues(`${INDEXED_ADDRESS}.postcode`),
    ),
  );

  const [selectedAddress, setSelectedAddress] = useState<TAddress>();

  useEffect(() => {
    const postcodeIsValidAndNotCrownDependency =
      verifyUKPostcode(searchQuery) && !verifyUKCrownPostcode(searchQuery);

    if (postcodeIsValidAndNotCrownDependency) {
      const postcode = stripPostcodeFromAddress(searchQuery);

      if (postcode) {
        lookupPostcode(postcode).catch((e) => {
          console.error(e, 'failed to fetch postcode info');
        });
      }
    } else {
      setAddresses([]);
    }
  }, [searchQuery]);

  const lookupPostcode = async (postcode: string) => {
    try {
      setIsLoading(true);
      const addressResults = fetchGetPostcodeLookup({
        query: { postcode },
      });

      setAddresses((await addressResults).data);
    } catch {
      setAddresses([]);
    }
    setIsLoading(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isLoading) {
      e.preventDefault();
    }
  };

  const renderOptions = useCallback(() => {
    if (isLoading) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner dataTestId="postcode-spinner" />
        </div>
      );
    }

    if (addresses.length === 0) {
      const emptyOption =
        searchQuery.length > 5
          ? 'Invalid postcode entered'
          : 'Enter your home postcode';

      return <p className={styles.noResults}>{emptyOption}</p>;
    }

    return addresses.map((address, index) => (
      <Combobox.Option
        className={({ active }) =>
          clsx(styles.option, 'fs-mask dd-privacy-mask', {
            [styles.active]: active,
          })
        }
        key={index}
        value={address}
      >
        {displayAddressValue(address)}
      </Combobox.Option>
    ));
  }, [addresses, isLoading, searchQuery]);

  const buildAddressValue = () => {
    const address = {
      house_name: getValues(`${INDEXED_ADDRESS}.houseName`),
      house_number: getValues(`${INDEXED_ADDRESS}.houseNumber`),
      street_line_1: getValues(`${INDEXED_ADDRESS}.streetLine1`),
      town: getValues(`${INDEXED_ADDRESS}.town`),
      postcode: getValues(`${INDEXED_ADDRESS}.postcode`),
    };

    return displayAddressValue(selectedAddress || address);
  };

  const displayAddressValue = (address: TAddress) => {
    const { house_name, house_number, street_line_1, town, postcode } = address;

    // TODO remove the below conditions once we fix undefined types for address details
    const addressLine = `${house_name && house_name} ${
      house_number && house_number
    } ${street_line_1 && street_line_1}`;

    return buildAddressLabel([addressLine, town, postcode]);
  };

  const handleClearAddress = () => {
    setSearchQuery('');
    resetAddress();
    setShowDefaultAddress(false);
  };

  const toggleManualAddress = () => {
    setIsManualAddress((prevManualState) => !prevManualState);
  };

  const onChange = (value: TAddress) => {
    setSelectedAddress(value);

    if (!value) return;

    if (value.house_name) {
      setValue(`${INDEXED_ADDRESS}.houseName`, value.house_name, {
        shouldValidate: true,
      });
    }

    if (value.house_number) {
      setValue(`${INDEXED_ADDRESS}.houseNumber`, value.house_number, {
        shouldValidate: true,
      });
    }

    if (value.street_line_1) {
      setValue(`${INDEXED_ADDRESS}.streetLine1`, value.street_line_1, {
        shouldValidate: true,
      });
    }

    if (value.street_line_2) {
      setValue(`${INDEXED_ADDRESS}.streetLine2`, value.street_line_2, {
        shouldValidate: true,
      });
    }

    if (value.ptc_abs_code) {
      setValue(`${INDEXED_ADDRESS}.equifaxToken`, value.ptc_abs_code);
    }

    if (value.town) {
      setValue(`${INDEXED_ADDRESS}.town`, value.town, {
        shouldValidate: true,
      });
    }

    if (value.postcode) {
      setValue(`${INDEXED_ADDRESS}.postcode`, value.postcode, {
        shouldValidate: true,
      });
    }
  };

  const resetAddress = () => {
    setSelectedAddress(undefined);

    setValue(`${INDEXED_ADDRESS}.postcode`, '');
    setValue(`${INDEXED_ADDRESS}.houseName`, undefined);
    setValue(`${INDEXED_ADDRESS}.houseNumber`, undefined);
    setValue(`${INDEXED_ADDRESS}.streetLine1`, undefined);
    setValue(`${INDEXED_ADDRESS}.streetLine2`, undefined);
    setValue(`${INDEXED_ADDRESS}.town`, undefined);
    setValue(`${INDEXED_ADDRESS}.equifaxToken`, undefined);
  };

  return (
    <>
      <Combobox nullable onChange={onChange} disabled={isManualAddress}>
        <div className={styles.postcodeLabelContainer}>
          <div className={styles.postcodeInputLabel}>
            <Combobox.Label>{postcodeLabel || 'Home address'}</Combobox.Label>
          </div>
          <ButtonLink
            type="button"
            className={styles.manualAddressButton}
            onClick={toggleManualAddress}
          >
            {isManualAddress ? 'Search for my address' : 'Enter manually'}
          </ButtonLink>
        </div>
        <div className={styles.postcodeSearchContainer}>
          <div className={clsx(styles.postcodeSearch)}>
            {selectedAddress || showDefaultAddress ? (
              <>
                <Input
                  className={`${styles.postcodeInput} fs-mask`}
                  disabled
                  value={buildAddressValue()}
                  error={
                    errors?.residentialAddresses?.[addressIndex]?.postcode
                      ?.message
                  }
                />
                <Button
                  className={styles.clearButton}
                  type="reset"
                  variant="tertiary"
                  size="small"
                  data-testid="clear-button-postcode"
                  onClick={handleClearAddress}
                >
                  <IconPlus
                    className={styles.clearButtonIcon}
                    height="24"
                    width="24"
                  />
                </Button>
              </>
            ) : (
              <>
                <Combobox.Input
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                  }}
                  as={Fragment}
                >
                  <Input
                    iconRight={<IconZoomOutline />}
                    placeholder="Search for a postcode"
                    className={`${styles.postcodeInput} fs-mask`}
                    data-testid="postcodeSearch"
                    onKeyDown={handleKeyDown}
                    error={
                      errors?.residentialAddresses?.[addressIndex]?.postcode
                        ?.message
                    }
                  />
                </Combobox.Input>
              </>
            )}
            {searchQuery && (
              <Combobox.Options className={styles.options}>
                {renderOptions()}
              </Combobox.Options>
            )}
          </div>
          {renderRemoveAddressComponent && renderRemoveAddressComponent()}
        </div>
      </Combobox>
      {isManualAddress && <ManualAddressInput addressIndex={addressIndex} />}
    </>
  );
};
